/**
 * Drives play/pause on a lazily-loaded <video> from its viewport visibility.
 *
 * WEB-2593: off-screen looping videos must be paused so mobile GPUs don't
 * exhaust their limited concurrent decode streams (and the RAM behind them).
 *
 * `shouldPlay` (default true) is an extra gate for callers that want to keep a
 * loaded, on-screen video paused anyway — e.g. a carousel that only lets the
 * active slide decode so iOS never spins up more than one decoder at a time.
 *
 * `play()` returns a promise that rejects with AbortError when a pause()
 * interrupts it (common during fast scrolling); we swallow that rejection.
 */
export const applyVideoPlayback = (
  videoEl,
  { isLoaded, isVisible, shouldPlay = true, limiter }
) => {
  if (!videoEl || !isLoaded) return;

  if (isVisible && shouldPlay) {
    // Reserve a decode slot first; the limiter may pause an older video to make
    // room before this one starts.
    if (limiter) limiter.acquire(videoEl);
    const played = videoEl.play();
    if (played && typeof played.catch === "function") {
      played.catch(() => {});
    }
  } else {
    if (limiter) limiter.release(videoEl);
    videoEl.pause();
  }
};
